import debug from 'debug';

import { velocityTransitions } from 'Minetilbud/Shared/Components/VelocityTransitions/VelocityTransitions';
import Templates from './Templates';

const log = debug('PopoverService');
const templates = new Templates();

class PopoverService {
	/**
	 * Creates an instance of Popover Service
	 * 
	 * @param {Object} content
	 * @param {string} content.template Markup to show in the popover
	 * @param {string} content.primaryColor
	 * @param {number} content.pointerX event.clientX
	 * @param {number} content.pointerY event.clientY
	 * @param {Function} [content.callBack]
	 * @param {string} [content.class] Override CSS Class
	 * @param {Object} [content.event] The event that triggered the popover
	 * @param {string} [content.iFrameListerSourceId] iframe list source id
	 * @param {string} [content.headline]
	 * @param {string} [content.headerTextColor]
	 * @param {string} [content.clickedEvent='popover-service-clicked'] Event that fires when content in popover is clicked
	 * @param {string} [content.removedEvent='popover-service-removed'] Event that fires when popover closes
	 */
	constructor(content) {
		this.content = content;

		this.arrowHeight = 20;
		this.arrowWidth = 15;
		this.indent = 40;
		this.heightOffset = 10;	// the height offset to the top of the screen

		this.handler = this.Handler.bind(this);
		this.remove = this.Remove.bind(this);
		
		this.popover = document.querySelector('[data-js="PopoverService"]');
		this.closeIcon

		this.Init();
	}

	Events() {
		document.addEventListener('click', this.remove, false);
		this.popover.addEventListener('click', this.handler, false);

		if (this.closeIcon) {
			this.closeIcon.addEventListener('click', this.remove, false);
		}
		
		const iframeDoc = this.GetIframeDocument();
		if(iframeDoc) {
			iframeDoc.addEventListener('click', this.remove, false);
		}
	}

	EventsRemove() {
		document.removeEventListener('click', this.remove, false);
		this.popover.removeEventListener('click', this.handler, false);

		if (this.closeIcon) {
			this.closeIcon.removeEventListener('click', this.remove, false);
		}
		
		const iframeDoc = this.GetIframeDocument();
		if(iframeDoc) {
			iframeDoc.removeEventListener('click', this.remove, false);
		}
	}

	/**
	 * @return {object} The DOM for the popover
	 */
	Get() {
		return this.popover;
	}

	/**
	 * @return {Object} iframe contentDocument or null
	 */
	GetIframeDocument() {
		const iframe = document.getElementById(this.content.iFrameListerSourceId);
		if(iframe && iframe.contentDocument) {
			return iframe.contentDocument;
		}
		return null;
	}

	Handler(event) {
		const customEvent = new CustomEvent(this.content.clickedEvent || 'popover-service-clicked', {
			detail: {
				event: event,
				target: event.target
			}
		});

		log('Handler() - customEvent', customEvent);
		document.dispatchEvent(customEvent);
	}

	/**
	 * @deprecated use Remove() instead
	 */
	Hide() {
		this.Remove();
	}

	Init() {
		if (this.IsVisible()) {
			this.EventsRemove();
			document.body.removeChild(this.popover);
		}

		document.body.insertAdjacentHTML('afterbegin', templates.Markup(this.content));

		this.popover = document.querySelector('[data-js="PopoverService"]');
		this.closeIcon = this.popover.querySelector('[data-js="PopoverServiceCloseIcon"]');

		this.SetPosition();
		this.Show();
	}

	/**
	 * @return {boolean} Popover exist
	 */
	IsVisible() {
		return document.body.contains(this.popover);
	}

	/**
	 * Hides the popover and removes it from the DOM
	 */
	Remove(event) {
		if (event && this.popover.contains(event.target) && event.target.closest('[data-js]') !== this.closeIcon) return;
		if (!this.IsVisible()) return;

		velocityTransitions.velocity(this.popover, 'ShrinkOut', {
			duration: 450,
			easing: 'easeOutCubic' 
		});

		this.EventsRemove();

		const customEvent = new CustomEvent(this.content.removedEvent || 'popover-service-removed', {
			detail: {
				event: event
			}
		});
		log('Remove() - customEvent', customEvent);
		document.dispatchEvent(customEvent);

		setTimeout(() => {
			if (!document.body.contains(this.popover)) return;
			document.body.removeChild(this.popover);
		}, 600);
	}

	/**
	 * Sets the position of the popover based on the pointer coordinates
	 */
	SetPosition() {
		this.popover.style.display = 'block';

		const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
		const popoverRect = this.popover.getBoundingClientRect();

		const indention = popoverRect.width + this.content.pointerX - this.indent > window.innerWidth ? popoverRect.width - this.indent : this.indent;
		const distanceX = this.content.pointerX;
		let distanceY = window.innerHeight - this.content.pointerY - scrollTop;

		if (this.content.iFrameListerSourceId) {
			distanceY = this.SetPositionIframe();
		}

		this.popover.style.bottom = distanceY + this.arrowHeight + 'px';
		this.popover.style.left = distanceX - indention - (this.arrowWidth / 2) + 'px';
		this.popover.querySelector('[data-js="TriangleDown"]').style.left = indention + 'px';

		if (!this.content.event) return;

		// Position of the triggering element relative to the document minus the popover elements height
		const difference = this.content.pointerY + scrollTop - (popoverRect.height + this.arrowHeight + this.heightOffset);

		// The popover element is positioned outside the documents top, so make sure to limit the height so that all content is visible 
		if (difference < 0) {
			const maxHeight = popoverRect.height + difference - this.heightOffset - 39; // 39 is height of the header
			this.popover.querySelector('[data-js="PopoverServiceList"]').style.maxHeight = `${ maxHeight }px`;
		}
	}

	SetPositionIframe() {
		const iframe = document.getElementById(this.content.iFrameListerSourceId);
		if(!iframe || !iframe.contentDocument) {
			log('SetPositionIframe() - iframe with id does not exist', this.content.iFrameListerSourceId);
			return;
		}

		const iframeRect = iframe.getBoundingClientRect();
		const iframeDocumentRect = iframe.contentDocument.querySelector('body').getBoundingClientRect();

		const distanceY = (window.innerHeight - iframeRect.bottom) + (iframeDocumentRect.height - this.content.pointerY);
		return distanceY;
	}

	/**
	 * Displays the popover
	 */
	Show() {
		velocityTransitions.velocity(this.popover, 'ShrinkIn', {
			duration: 200,
			easing: 'easeOutCubic',
			complete: () => {
				setTimeout(() => {
					this.Events();

					if(typeof this.content.callBack === 'function') {
						this.content.callBack();
					}
				}, 400);
			}
		});
	}
}

export default PopoverService;
